import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { usePartnerPipelineStore } from '../../lib/stores/partnerPipelineStore';
import { usePermissions } from '../../lib/hooks/usePermissions';
import { useApp } from '../AppProvider';
import { StatusManagerSkeleton } from '../ui/SkeletonLoader';

interface PartnerStageSlotProps {
  partnerId: string;
  /**
   * Fix-wave "Partner drawer unificato" (finding MEDIUM): moveToStage/addToPipeline
   * scrivono già la riga timeline su sales_activities, ma nel drawer (isModal,
   * realtime disattivo) la cronologia non si aggiorna finché non si riapre. Callback
   * opzionale invocata dopo un cambio fase riuscito così il chiamante (i layout, che
   * la inoltrano dal wiring del composer già esistente) può ricaricare la timeline.
   */
  onStageChanged?: () => void | Promise<void>;
}

/**
 * Slot fase pipeline per il drawer partner (Task 4 "Partner drawer unificato").
 * Sostituisce LeadStatusManager nel ramo entityType === 'partner' (non applicabile:
 * niente lead_stage/lead_source/lead_score sui partner), mostrando pipeline+fase
 * correnti da `partner_pipeline_memberships` con un select per cambiare fase
 * (moveToStage, che scrive già la riga timeline) o aggiungere il partner a una
 * pipeline (addToPipeline). Se l'org non ha pipeline partner attive, non
 * renderizza nulla.
 *
 * Nota tecnica: usePartnerPipelineStore tiene stage/membership di UNA sola
 * pipeline alla volta (stesso pattern del Kanban in PartnersPage.tsx/
 * PartnerKanban.tsx). Questo componente fa una query diretta e mirata per
 * scoprire in quale pipeline si trova il partner, poi delega a
 * selectPipeline/moveToStage/addToPipeline dello store per lo stato e la
 * scrittura, coerente col resto della feature partner.
 */
export function PartnerStageSlot({ partnerId, onStageChanged }: PartnerStageSlotProps) {
  const { toast } = useApp();
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission('external_partners', 'update');

  const {
    pipelines,
    stages,
    memberships,
    fetchPipelines,
    selectPipeline,
    moveToStage,
    addToPipeline,
  } = usePartnerPipelineStore();

  const [pipelinesLoading, setPipelinesLoading] = useState(true);
  const [resolvedPipelineId, setResolvedPipelineId] = useState<string | null>(null);
  const [resolving, setResolving] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    fetchPipelines().finally(() => setPipelinesLoading(false));
  }, [fetchPipelines]);

  // Scopre in quale pipeline si trova il partner (query diretta, indipendente
  // dallo store). Fix-wave "Partner drawer unificato" (finding MINOR): quando il
  // partner appartiene a 2+ pipeline la scelta non era deterministica (nessun
  // order, riga arbitraria). Ora: se il partner ha una membership nella pipeline
  // già selezionata nello store (es. quella aperta nel Kanban da cui si è aperto
  // il drawer), si preferisce quella; altrimenti si ordina per entered_at
  // ascendente e si prende la prima (appartenenza più vecchia). Se in nessuna
  // pipeline, usa la pipeline di default dell'org come target per un eventuale
  // addToPipeline.
  useEffect(() => {
    if (pipelinesLoading || pipelines.length === 0) return;
    let cancelled = false;
    setResolving(true);

    (async () => {
      // Lettura one-shot (non reattiva) della pipeline correntemente selezionata nello
      // store: evita di aggiungere selectedPipelineId alle dipendenze dell'effect, che
      // farebbe ripartire la risoluzione ad ogni selectPipeline() (anche verso lo stesso
      // target) dato che questo componente sottoscrive l'intero store.
      const { selectedPipelineId } = usePartnerPipelineStore.getState();

      const { data } = await supabase
        .from('partner_pipeline_memberships')
        .select('pipeline_id, entered_at')
        .eq('partner_id', partnerId)
        .order('entered_at', { ascending: true });

      if (cancelled) return;

      const partnerMemberships = data || [];
      const currentSelectionMembership = selectedPipelineId
        ? partnerMemberships.find(m => m.pipeline_id === selectedPipelineId)
        : undefined;

      const targetPipelineId = currentSelectionMembership?.pipeline_id
        || partnerMemberships[0]?.pipeline_id
        || pipelines.find(p => p.is_default)?.id
        || pipelines[0].id;

      setResolvedPipelineId(targetPipelineId);
      await selectPipeline(targetPipelineId);
      if (!cancelled) setResolving(false);
    })();

    return () => { cancelled = true; };
  }, [partnerId, pipelinesLoading, pipelines, selectPipeline]);

  const currentMembership = useMemo(
    () => memberships.find(m => m.partner_id === partnerId),
    [memberships, partnerId]
  );
  const currentStage = useMemo(
    () => (currentMembership ? stages.find(s => s.id === currentMembership.pipeline_stage_id) : undefined),
    [stages, currentMembership]
  );
  const currentPipeline = useMemo(
    () => pipelines.find(p => p.id === resolvedPipelineId),
    [pipelines, resolvedPipelineId]
  );

  const handleStageSelect = async (newStageId: string) => {
    if (!newStageId || !canEdit || isUpdating) return;
    setIsUpdating(true);
    try {
      const success = currentMembership
        ? await moveToStage(partnerId, newStageId)
        : await addToPipeline(partnerId, newStageId);

      if (success) {
        toast.success(currentMembership ? 'Fase aggiornata' : 'Partner aggiunto alla pipeline');
        await onStageChanged?.();
      } else {
        const errorMessage = usePartnerPipelineStore.getState().error || 'Aggiornamento non riuscito';
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('[PartnerStageSlot] Errore aggiornamento fase:', error);
      toast.error('Errore durante l\'aggiornamento della fase');
    } finally {
      setIsUpdating(false);
    }
  };

  if (pipelinesLoading) {
    return <StatusManagerSkeleton />;
  }

  // Nessuna pipeline partner configurata per l'org: nessuna sostituzione di
  // LeadStatusManager necessaria, lo slot non renderizza nulla.
  if (pipelines.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3 overflow-visible">
      <h3 className="text-xs font-medium text-[#787774] dark:text-gray-400 uppercase tracking-wide">
        Gestione
      </h3>

      <div className="space-y-1">
        <label className="block text-xs text-[#787774] dark:text-gray-400">
          Fase pipeline
        </label>
        <div className={`flex items-center gap-2 px-2 py-1.5 rounded transition-colors ${
          canEdit ? 'hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/[0.05]' : 'opacity-60'
        }`}>
          <div
            className="w-2.5 h-2.5 rounded-full flex-shrink-0"
            style={{ backgroundColor: currentStage?.color || '#6B7280' }}
          />
          <select
            value={currentStage?.id || ''}
            onChange={(e) => handleStageSelect(e.target.value)}
            disabled={!canEdit || isUpdating || resolving || stages.length === 0}
            className="flex-1 bg-transparent text-sm text-[#37352F] dark:text-white focus:outline-none disabled:cursor-not-allowed"
          >
            {!currentMembership && <option value="">Non in pipeline</option>}
            {stages.map(stage => (
              <option key={stage.id} value={stage.id}>{stage.name}</option>
            ))}
          </select>
          {(isUpdating || resolving) && (
            <div className="animate-spin rounded-full h-3 w-3 border-t border-b border-[#337EA9] flex-shrink-0" />
          )}
        </div>
        {currentPipeline && (
          <p className="text-[10px] text-[#787774] dark:text-gray-500 px-2">
            Pipeline: {currentPipeline.name}
          </p>
        )}
      </div>
    </div>
  );
}
